import React, { useState, useEffect, useCallback, useRef } from "react";
import { Button } from "../components/Button.jsx";
import { usePlayer, useGame } from "@empirica/core/player/classic/react";
import { savePlayerMeta } from "../utils/savePlayerMeta.js";
import { getTaskConfig } from "../utils/taskConfig.js";
import {
  getCompensationConfig,
  getFakeLobbyConfig,
  getPostConfirmPolishMode,
  getPreLobbyMode,
  getPostConfirmWriteupMode,
  getReflectionTiming,
  isBaseRateGroup3Condition,
  isBaseRateStaticSyntheticGroupCondition,
  isFourTaskPilotCondition,
  isPasteEnabledNogroupCondition,
  usesBriefInitialRationale,
  usesPostInitialConditionInstructions,
  usesPostPeerClosedChoice,
  suppressesPostConfirmWriteup,
} from "../utils/conditionUtils.js";
import {
  flushBehaviorTracking,
  recordSubmittedIntegrityText,
  useIntegrityTracking,
} from "../utils/integrityTracking.js";
import {
  PredictionButtonPage,
  countWords,
  formatPredictionValue,
  normalizePredictionValue,
} from "../components/PredictionButtonPage.jsx";
import {
  PRE_LOBBY_REFLECTION_KEYS,
  ReflectionAssistantChat,
  ReflectionTemplateQuestionnaire,
} from "../components/ReflectionActivity.jsx";
import { ActivityInstructions } from "./ActivityInstructions.jsx";
import { FourTaskPilotTask } from "./FourTaskPilotTask.jsx";
import { CollapsibleTaskDetails } from "../components/TaskDetailsPanel.jsx";
import quizData from "../data/quizCases.json";

function FakeLobbyPage({
  player,
  minSeconds = 3,
  maxSeconds = 10,
  basePaymentAmountText = "$2",
  onComplete,
}) {
  const completionTriggeredRef = useRef(false);
  const [timeWaited, setTimeWaited] = useState(() => {
    const startedAt = Number(player.get("preLobbyFakeLobbyStartedAt") || 0);
    if (startedAt <= 0) {
      return 0;
    }
    return Math.max(0, Math.floor((Date.now() - startedAt) / 1000));
  });

  useEffect(() => {
    let startedAt = Number(player.get("preLobbyFakeLobbyStartedAt") || 0);
    let durationSeconds = Number(
      player.get("preLobbyFakeLobbyDurationSeconds") || 0
    );

    const durationIsValid =
      Number.isInteger(durationSeconds) &&
      durationSeconds >= minSeconds &&
      durationSeconds <= maxSeconds;
    if (!durationIsValid) {
      durationSeconds =
        Math.floor(Math.random() * (maxSeconds - minSeconds + 1)) + minSeconds;
    }
    if (!Number.isFinite(startedAt) || startedAt <= 0) {
      startedAt = Date.now();
    }

    const endAt = startedAt + durationSeconds * 1000;
    player.set("preLobbyFakeLobbyStartedAt", startedAt);
    player.set("preLobbyFakeLobbyDurationSeconds", durationSeconds);
    player.set("preLobbyFakeLobbyEndsAt", endAt);
    player.set("preLobbyFakeLobbyCompleted", false);

    const updateFakeLobbyState = () => {
      const elapsed = Math.max(0, Math.floor((Date.now() - startedAt) / 1000));
      setTimeWaited(elapsed);

      if (Date.now() >= endAt && !completionTriggeredRef.current) {
        completionTriggeredRef.current = true;
        onComplete();
      }
    };

    updateFakeLobbyState();
    const timer = setInterval(updateFakeLobbyState, 250);
    return () => clearInterval(timer);
  }, [maxSeconds, minSeconds, onComplete, player]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  return (
    <div className="text-center space-y-6">
      <div className="max-w-2xl mx-auto">
        <h2 className="text-2xl font-bold text-gray-900 mb-4">
          Preparing Group Review
        </h2>

        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-6">
          <p className="text-lg text-blue-800 mb-4">
            We are preparing the other participants' responses for your review.
          </p>

          <div className="text-sm text-blue-700 space-y-2">
            <p>
              You will continue shortly. Your {basePaymentAmountText} base pay is
              guaranteed after completing the study.
            </p>
            <p>
              <strong>Elapsed time:</strong>{" "}
              <span className="font-mono text-lg text-gray-700">
                {formatTime(timeWaited)}
              </span>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

function formatCurrency(value) {
  if (value === null || value === undefined || Number.isNaN(Number(value))) return "N/A";
  return `$${Number(value).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
}

function formatPercent(value) {
  if (value === null || value === undefined || Number.isNaN(Number(value))) return "N/A";
  return `${Number(value).toFixed(2).replace(/\\.00$/, "")}%`;
}

function formatInt(value) {
  if (value === null || value === undefined || Number.isNaN(Number(value))) return "N/A";
  return `${parseInt(Number(value), 10)}`;
}

function hasPredictionChanged(beforeValue, afterValue, taskConfig = {}) {
  const before = normalizePredictionValue(beforeValue, taskConfig);
  const after = normalizePredictionValue(afterValue, taskConfig);
  return Boolean(before) && Boolean(after) && before !== after;
}

function getChangedAnswerConfidenceValidationError({
  changedAnswer,
  finalConfidence,
  initialAnswerConfidence,
  config,
}) {
  if (!changedAnswer || !config) return "";

  const parsedInitialAnswerConfidence = Number(initialAnswerConfidence);
  const parsedMin = Number(config.min);
  const parsedMax = Number(config.max);
  const effectiveMin = Number.isFinite(parsedMin) ? parsedMin : 0;
  const effectiveMax = Number.isFinite(parsedMax) ? parsedMax : 100;
  const requireWholeNumber = config.requireWholeNumber === true;
  const defaultRangeErrorText = requireWholeNumber
    ? `Please enter a whole-number confidence between ${effectiveMin} and ${effectiveMax} for your initial answer.`
    : `Please enter a confidence between ${effectiveMin} and ${effectiveMax} for your initial answer.`;
  const initialAnswerConfidenceInRange =
    Number.isFinite(parsedInitialAnswerConfidence) &&
    parsedInitialAnswerConfidence >= effectiveMin &&
    parsedInitialAnswerConfidence <= effectiveMax &&
    (!requireWholeNumber || Number.isInteger(parsedInitialAnswerConfidence));

  if (!initialAnswerConfidenceInRange) {
    return config.errorText || defaultRangeErrorText;
  }

  const parsedFinalConfidence = Number(finalConfidence);
  const parsedCombinedMax = Number(config.combinedMax);
  if (
    Number.isFinite(parsedCombinedMax) &&
    (!Number.isFinite(parsedFinalConfidence) ||
      parsedFinalConfidence + parsedInitialAnswerConfidence > parsedCombinedMax)
  ) {
    return config.errorText || `The two confidence values must sum to ${parsedCombinedMax} or less.`;
  }

  return "";
}

function QuizStage({ onComplete }) {
  const player = usePlayer();
  const cases = Array.isArray(quizData?.cases) ? quizData.cases : [];
  const existingAnswers = player.get("quizAnswers") || [];
  const [answers, setAnswers] = useState(existingAnswers);
  const initialIndex = Math.min(existingAnswers.length, Math.max(cases.length - 1, 0));
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [selected, setSelected] = useState(() => {
    const existing = existingAnswers.find((a) => a.caseIndex === initialIndex);
    return existing?.answer || null;
  });

  useEffect(() => {
    if (quizData?.seed) {
      player.set("quizSeed", quizData.seed);
    }
  }, []);

  if (cases.length === 0) {
    return (
      <div className="max-w-3xl mx-auto p-6">
        <h2 className="text-2xl font-bold mb-6">Quiz</h2>
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-700">Quiz data is missing. Please contact the researcher.</p>
        </div>
      </div>
    );
  }

  const currentCase = cases[currentIndex];
  const total = cases.length;

  const handleNext = () => {
    if (!selected) return;
    const timestamp = Date.now();
    const answerRecord = {
      caseIndex: currentIndex,
      sourceRow: currentCase?.sourceRow,
      caseId: currentCase?.id,
      answer: selected,
      answeredAt: timestamp,
    };
    const updated = answers.filter((a) => a.caseIndex !== currentIndex).concat(answerRecord);
    updated.sort((a, b) => a.caseIndex - b.caseIndex);
    setAnswers(updated);
    player.set("quizAnswers", updated);

    if (currentIndex + 1 < total) {
      const nextIndex = currentIndex + 1;
      setCurrentIndex(nextIndex);
      const nextExisting = updated.find((a) => a.caseIndex === nextIndex);
      setSelected(nextExisting?.answer || null);
      player.set("quizCurrentIndex", nextIndex);
    } else {
      player.set("quizCompleted", true);
      player.set("quizCompletedAt", timestamp);
      savePlayerMeta({ player, name: "quizAnswers", value: updated, debugTag: "Quiz" });
      onComplete();
    }
  };

  return (
    <div className="max-w-3xl mx-auto p-6">
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
        <h2 className="text-2xl font-bold text-amber-900 mb-1">Knowledge Check Quiz</h2>
        <p className="text-sm text-amber-800">
          This is a short 10-question quiz to assess baseline knowledge. It is separate from the main discussion case.
          You will not see answers during the quiz.
        </p>
      </div>
      <p className="text-sm text-gray-600 mb-6">
        Question {currentIndex + 1} of {total}
      </p>

      <div className="bg-white rounded-lg shadow-md p-6 mb-6 border border-amber-200">
        <h4 className="font-medium text-gray-900 mb-3">Quiz Case Details:</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm text-gray-800">
          <div>
            <strong>Listed loan amount</strong> (the total amount the borrower is requesting):{" "}
            {formatCurrency(currentCase.loanAmount)}
          </div>
          <div>
            <strong>Months since most recent inquiry</strong> (how long it has been since a lender last checked the credit report):{" "}
            {`${formatInt(currentCase.monthsSinceInquiry)} months`}
          </div>
          <div>
            <strong>Credit utilization ratio</strong> (the percentage of total available credit that is currently being used):{" "}
            {formatPercent(currentCase.creditUtilization)}
          </div>
          <div>
            <strong>Total open to buy on revolving bankcards</strong> (the remaining credit available on credit cards after current balances):{" "}
            {formatCurrency(currentCase.openToBuy)}
          </div>
          <div>
            <strong>Ratio of total current balance to credit limit</strong> (how close all balances are to the combined credit limits):{" "}
            {formatPercent(currentCase.balanceToLimit)}
          </div>
          <div>
            <strong>Number of open revolving accounts</strong> (how many active credit card or similar revolving credit accounts there are):{" "}
            {formatInt(currentCase.openRevolving)}
          </div>
          <div>
            <strong>Number of payments on the loan</strong> (the length of the loan repayment term in months):{" "}
            {currentCase.term}
          </div>
          <div>
            <strong>30+ days past-due incidences (past 2 years)</strong> (how many times a payment was at least 30 days late in the last 2 years):{" "}
            {formatInt(currentCase.delinq30_2y)}
          </div>
          <div>
            <strong>Interest rate on the loan</strong> (the yearly cost of borrowing expressed as a percentage of the amount borrowed):{" "}
            {formatPercent(currentCase.interestRate)}
          </div>
          <div>
            <strong>Debt to income ratio</strong> (the share of monthly income that goes toward paying debts):{" "}
            {formatPercent(currentCase.dti)}
          </div>
          <div className="md:col-span-2">
            <strong>Loan description</strong> (the borrower’s own explanation of how they plan to use the loan):{" "}
            {currentCase.description}
          </div>
        </div>
      </div>

      <p className="text-xl mb-4">What is your prediction for this quiz case?</p>
      <div className="flex justify-center gap-4 mb-6">
        <Button
          className={`px-8 py-4 ${selected === "Default" ? "bg-blue-700" : "bg-blue-500 hover:bg-blue-600"}`}
          handleClick={() => setSelected("Default")}
        >
          Default
        </Button>
        <Button
          className={`px-8 py-4 ${selected === "Not Default" ? "bg-blue-700" : "bg-blue-500 hover:bg-blue-600"}`}
          handleClick={() => setSelected("Not Default")}
        >
          Not Default
        </Button>
      </div>

      {selected && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6 text-center">
          <p className="text-lg">
            You selected: <strong className="text-blue-700">{selected}</strong>
          </p>
        </div>
      )}

      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex justify-center">
          <Button
            handleClick={handleNext}
            className="px-8 py-3 bg-green-500 hover:bg-green-600"
            disabled={!selected}
          >
            {currentIndex + 1 === total ? "Finish Quiz" : "Next Question"}
          </Button>
        </div>
        {!selected && (
          <p className="text-sm text-gray-500 mt-2 text-center">
            Please select your prediction before continuing.
          </p>
        )}
      </div>
    </div>
  );
}

function getInitialPredictionPageCopy(taskConfig = {}) {
  if (taskConfig.initialPredictionDescription !== undefined) {
    return {
      description: taskConfig.initialPredictionDescription,
      introPrompt: "",
    };
  }

  if (taskConfig.studyObjectiveText) {
    return {
      description: "Task Description",
      introPrompt: "",
    };
  }

  return {
    description: taskConfig.objectiveText || "",
    introPrompt: taskConfig.introPrompt || "",
  };
}

function RationaleConfirmationPage({
  prediction,
  rationale,
  onRationaleChange,
  onSubmit,
  transcriptMessages = [],
  preLobbyMode = "",
  writeupMode = "",
  polishMode = "",
  title = "Review Your Rationale",
  buttonText = "Continue to Lobby",
  integrityFieldPrefix = "rationale-confirmation",
  minimumWordCount = 1,
  collapseTranscriptByDefault = false,
  showDuringGroupReview = false,
  // Non-null only where the task description has already been presented in
  // full, which is every screen after the first prediction page.
  taskConfig = null,
}) {
  const trimmedRationale = (rationale || "").trim();
  const requiredWordCount = Math.max(1, Math.floor(Number(minimumWordCount) || 1));
  const rationaleWordCount = countWords(trimmedRationale);
  const hasValidRationale = rationaleWordCount >= requiredWordCount;
  const rationaleValidationMessage =
    requiredWordCount > 1
      ? `Please write at least ${requiredWordCount} words before continuing.`
      : "Please provide a rationale before continuing.";
  const orderedMessages = [...transcriptMessages].sort(
    (a, b) => Number(a?.createdAt || 0) - Number(b?.createdAt || 0)
  );
  const isTemplateMode = preLobbyMode === "template";
  const isConvinceOthersWriteup = writeupMode === "convince-others";
  const isGroupShareableWriteup = writeupMode === "group-shareable";
  const hasPostConfirmPolishStep = Boolean(polishMode);
  const isLongformWriteup = requiredWordCount >= 50;
  const [showTranscript, setShowTranscript] = useState(!collapseTranscriptByDefault);

  const renderTemplatePreview = (message) => {
    const rawText = String(message?.text || "");
    const [questionLine, ...rest] = rawText.split("\nAnswer:");
    const questionText = questionLine.replace(/^Question:\s*/i, "").trim();
    const answerText = rest.join("\nAnswer:").trim();
    return (
      <div key={message?.id} className="border border-gray-200 rounded-md p-4 bg-gray-50">
        <div className="text-sm font-semibold text-gray-900 mb-2">
          {questionText || "Follow-up question"}
        </div>
        <div className="text-sm text-gray-700 whitespace-pre-wrap">
          {answerText || rawText}
        </div>
      </div>
    );
  };

  const renderDialogPreview = (message) => {
    const isAiMessage = message?.meta?.isAI === true || message?.role === "assistant";
    return (
      <div
        key={message?.id}
        className={`rounded-md p-4 ${
          isAiMessage ? "bg-blue-50 border border-blue-200" : "bg-green-50 border border-green-200"
        }`}
      >
        <div className="text-sm font-semibold text-gray-900 mb-2">
          {isAiMessage ? "AI Assistant" : "You"}
        </div>
        <div className="text-sm text-gray-700 whitespace-pre-wrap">
          {message?.text || message?.content || ""}
        </div>
      </div>
    );
  };

  return (
    <div className="max-w-3xl mx-auto p-6">
      <h2 className="text-2xl font-bold mb-6">{title}</h2>

      <CollapsibleTaskDetails taskConfig={taskConfig} scrollable />

      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <p className="text-lg mb-4">
          {isTemplateMode
            ? isConvinceOthersWriteup
              ? "Below are the follow-up answers you gave before confirming your estimate."
              : showDuringGroupReview
                ? "Below are the follow-up answers you gave before confirming your pre-review estimate."
                : "Below are the follow-up answers you gave before confirming your pre-discussion estimate."
            : isConvinceOthersWriteup
              ? "Below is the conversation you had before confirming your estimate."
              : showDuringGroupReview
                ? "Below is the conversation you had before confirming your pre-review estimate."
                : "Below is the conversation you had before confirming your pre-discussion estimate."}
        </p>
        <p className="text-lg mb-4">
          {isConvinceOthersWriteup
            ? "If you were to convince others that your estimate is correct, what would you say? Please write a detailed paragraph. Be as specific as possible about the key reasons, evidence, and assumptions behind your estimate."
            : showDuringGroupReview
            ? "Please write a detailed paragraph explaining the key reasons, evidence, and assumptions behind your estimate. Be as specific as possible."
            : isLongformWriteup
            ? "Please write a detailed paragraph that the group will see. Be as specific as possible about the key reasons, evidence, and assumptions behind your estimate."
            : "Please write 2-3 lines summarizing the rationale you want the group to see. You may reuse your previous wording or improve it."}
        </p>
        {!isConvinceOthersWriteup && (
          <p className="text-lg mb-4">
            {showDuringGroupReview
              ? hasPostConfirmPolishStep
                ? "After you submit this draft, you will review an AI-polished version and may edit it before continuing."
                : "This helps you organize your reasoning before you review the other participants' responses."
              : hasPostConfirmPolishStep
                ? "After you submit this draft, you will review an AI-polished version and choose the final text that appears as your opening message in the group discussion."
                : "This writeup will appear as an opening message in the group discussion so everyone can see your rationale."}
          </p>
        )}
        {prediction && (
          <p className="text-lg mb-4">
            Your confirmed prediction:{" "}
            <strong className="text-blue-700">{prediction}</strong>
          </p>
        )}

        {orderedMessages.length > 0 && (
          <div className="mb-6 rounded-lg border border-gray-200 bg-gray-50 p-4">
            <div className="flex items-center justify-between gap-3">
              <div>
                <div className="text-sm font-semibold text-gray-900">
                  {isTemplateMode ? "Your earlier questionnaire answers" : "Your earlier AI conversation"}
                </div>
                <div className="text-sm text-gray-600">
                  Review this only if you want to reference or revise your earlier reasoning.
                </div>
              </div>
              <button
                type="button"
                className="rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100"
                onClick={() => setShowTranscript((prev) => !prev)}
              >
                {showTranscript ? "Hide" : "Show"}
              </button>
            </div>
            {showTranscript ? (
              <div className="mt-4 space-y-3">
                {orderedMessages.map((message) =>
                  isTemplateMode ? renderTemplatePreview(message) : renderDialogPreview(message)
                )}
              </div>
            ) : (
              <p className="mt-4 text-sm text-gray-600">
                Hidden by default. Click Show if you want to review the earlier exchange before writing{" "}
                {isConvinceOthersWriteup
                  ? "your persuasive rationale."
                  : showDuringGroupReview
                    ? "your rationale."
                    : "your group-facing rationale."}
              </p>
            )}
          </div>
        )}

        <label className="block text-lg font-semibold mb-2">
          {isConvinceOthersWriteup
            ? `Detailed persuasive rationale (at least ${requiredWordCount} words)`
            : showDuringGroupReview
            ? `Your rationale (at least ${requiredWordCount} words)`
            : isLongformWriteup
            ? `Detailed rationale shown to the group (at least ${requiredWordCount} words)`
            : "2-3 line rationale shown to the group"}
        </label>
        <textarea
          className="w-full border border-gray-300 rounded-md p-3 min-h-[160px]"
          data-integrity-field={`${integrityFieldPrefix}-rationale`}
          value={rationale}
          onChange={(e) => onRationaleChange(e.target.value)}
          placeholder={
            isConvinceOthersWriteup
              ? "Write a detailed paragraph explaining the strongest case for your estimate, as if you were trying to convince someone else."
              : showDuringGroupReview
              ? "Write a detailed paragraph explaining the main reasons behind your estimate, the key facts you relied on, and why you reached that answer."
              : isLongformWriteup
              ? "Write a detailed paragraph the group will see. Explain the main reasons behind your estimate, the key facts you relied on, and why you think your answer is strongest."
              : "Summarize the main reason behind your estimate in 2-3 lines."
          }
        />
        {isLongformWriteup && (
          <p className="text-sm text-gray-600 mt-2">
            Current length: {rationaleWordCount} words. Please write as much detail as possible so{" "}
            {isConvinceOthersWriteup
              ? "someone else can clearly understand your reasoning."
              : showDuringGroupReview
                ? "you can compare your reasoning with the other participants' responses."
                : "the group can clearly understand your reasoning."}
          </p>
        )}
        {!hasValidRationale && (
          <p className="text-sm text-gray-500 mt-2">
            {rationaleValidationMessage}
          </p>
        )}
      </div>

      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex justify-center">
          <Button
            handleClick={() => {
              if (!hasValidRationale) {
                alert(rationaleValidationMessage);
                return;
              }
              onSubmit(trimmedRationale);
            }}
            className="px-8 py-3 bg-green-500 hover:bg-green-600"
            disabled={!hasValidRationale}
          >
            {buttonText}
          </Button>
        </div>
      </div>
    </div>
  );
}

function PolishReviewPage({
  originalRationale = "",
  initialFinalRationale = "",
  onSubmit,
  buttonText = "Continue to Lobby",
  continuationDestination = "the lobby",
  taskConfig = null,
}) {
  const player = usePlayer();
  const draftRationale = String(originalRationale || "").trim();
  const polishedRationale = String(
    player.get("preLobbyPolishedRationale") || ""
  ).trim();
  const polishErrors = Array.isArray(player.get("preLobbyPolishAiErrors"))
    ? player.get("preLobbyPolishAiErrors")
    : [];
  const latestPolishError =
    polishErrors.length > 0 ? polishErrors[polishErrors.length - 1] : null;
  const [finalRationale, setFinalRationale] = useState(() =>
    String(initialFinalRationale || polishedRationale || draftRationale).trim()
  );
  const [hasManualEdits, setHasManualEdits] = useState(false);
  const [lastSuggestedText, setLastSuggestedText] = useState(() =>
    String(initialFinalRationale || polishedRationale || draftRationale).trim()
  );

  useEffect(() => {
    if (!polishedRationale) return;

    const trimmedCurrent = String(finalRationale || "").trim();
    const shouldAutofill =
      !hasManualEdits ||
      !trimmedCurrent ||
      trimmedCurrent === lastSuggestedText ||
      trimmedCurrent === draftRationale;

    if (shouldAutofill) {
      setFinalRationale(polishedRationale);
      setLastSuggestedText(polishedRationale);
    }
  }, [
    polishedRationale,
    hasManualEdits,
    finalRationale,
    lastSuggestedText,
    draftRationale,
  ]);

  const trimmedFinalRationale = String(finalRationale || "").trim();
  const isWaitingForPolish = !polishedRationale && !latestPolishError;
  const isGroupReview = String(continuationDestination)
    .toLowerCase()
    .includes("group review");

  return (
    <div className="max-w-3xl mx-auto p-6">
      <h2 className="text-2xl font-bold mb-6">AI Polishing Stage</h2>

      <CollapsibleTaskDetails taskConfig={taskConfig} scrollable />

      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <p className="text-lg mb-4">
          The AI lightly polished your writeup for grammar, spelling, punctuation, and readability without changing the substance.
        </p>
        <p className="text-lg mb-4">
          Review the polished version below. You can edit it further or keep it as-is before continuing to {continuationDestination}.
        </p>

        <div className="mb-6 rounded-lg border border-gray-200 bg-gray-50 p-4">
          <div className="text-sm font-semibold text-gray-900 mb-2">
            Your original draft
          </div>
          <div className="text-sm text-gray-700 whitespace-pre-wrap">
            {draftRationale}
          </div>
        </div>

        {isWaitingForPolish && (
          <div className="mb-6 rounded-lg border border-blue-200 bg-blue-50 p-4">
            <div className="text-sm font-semibold text-blue-900 mb-2">
              Generating AI-polished version
            </div>
            <p className="text-sm text-blue-800">
              The editor below will update automatically as soon as the polished version arrives.
            </p>
          </div>
        )}

        {!isWaitingForPolish && latestPolishError && !polishedRationale && (
          <div className="mb-6 rounded-lg border border-yellow-200 bg-yellow-50 p-4">
            <div className="text-sm font-semibold text-yellow-900 mb-2">
              AI polish unavailable
            </div>
            <p className="text-sm text-yellow-800">
              We could not generate the polished version this time, so you can continue using your original draft or edit it manually.
            </p>
          </div>
        )}

        <label className="block text-lg font-semibold mb-2">
          {isGroupReview
            ? "Your final rationale"
            : "Final rationale shown to the group"}
        </label>
        <textarea
          className="w-full border border-gray-300 rounded-md p-3 min-h-[180px]"
          value={finalRationale}
          disabled={isWaitingForPolish}
          onChange={(e) => {
            setHasManualEdits(true);
            setFinalRationale(e.target.value);
          }}
          placeholder={
            isGroupReview
              ? "Your final rationale before continuing."
              : "The final rationale the group will see."
          }
        />
        {polishedRationale && (
          <p className="text-sm text-gray-600 mt-2">
            The editor was prefilled with the AI-polished version. You can still revise it before continuing.
          </p>
        )}
      </div>

      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex justify-center">
          <Button
            handleClick={() => {
              if (isWaitingForPolish) {
                return;
              }
              if (!trimmedFinalRationale) {
                alert("Please review the text before continuing.");
                return;
              }
              onSubmit(trimmedFinalRationale);
            }}
            className="px-8 py-3 bg-green-500 hover:bg-green-600"
            disabled={isWaitingForPolish || !trimmedFinalRationale}
          >
            {buttonText}
          </Button>
        </div>
      </div>
    </div>
  );
}

export function InitialPrediction({ next }) {
  const player = usePlayer();
  const game = useGame();
  const treatment = game?.get("treatment") || player?.get("treatment") || {};
  const factors = treatment.factors || {};
  const treatmentType = treatment.treatmentType || factors.treatmentType;
  const taskConfig = getTaskConfig(treatment);
  const treatmentNameExact = String(
    treatment.name ||
      treatment.treatmentName ||
      factors.name ||
      factors.treatmentName ||
      ""
  );
  const quizEnabled = typeof factors.skipQuiz === "boolean"
    ? !factors.skipQuiz
    : taskConfig.quizEnabled !== false;
  const [step, setStep] = useState(() => {
    if (!quizEnabled) return "initial";
    return player.get("quizCompleted") ? "initial" : "quiz";
  }); // "quiz", "initial", "activityInstructions", "aiChat", "confirm", "confirmRationale", "polishReview"
  const [selectedPredictionBeforeAI, setSelectedPredictionBeforeAI] = useState(null);
  const [selectedPredictionAfterAI, setSelectedPredictionAfterAI] = useState(null);
  const [initialExplanation, setInitialExplanation] = useState(() => player.get("preLobbyExplanation") || "");
  const [initialConfidence, setInitialConfidence] = useState(() => {
    const beforeAI = player.get("preLobbyConfidenceBeforeAI");
    const direct = player.get("preLobbyConfidence");
    const existing = beforeAI ?? direct;
    return existing === undefined || existing === null ? "" : String(existing);
  });
  const [confirmConfidence, setConfirmConfidence] = useState(() => {
    const afterAI = player.get("preLobbyConfidenceAfterAI");
    const direct = player.get("preLobbyConfidence");
    const existing = afterAI ?? direct;
    return existing === undefined || existing === null ? "" : String(existing);
  });
  const [confirmInitialAnswerConfidence, setConfirmInitialAnswerConfidence] = useState(() => {
    const existing = player.get("preLobbyConfidenceForInitialAnswerAfterAI");
    return existing === undefined || existing === null ? "" : String(existing);
  });
  const preLobbyMode = getPreLobbyMode(treatment);
  const postConfirmWriteupMode = getPostConfirmWriteupMode(treatment);
  const postConfirmPolishMode = getPostConfirmPolishMode(treatment);
  const compensation = getCompensationConfig(treatment);
  const fakeLobbyConfig = getFakeLobbyConfig(treatment);
  const collectRationaleSetting = (
    typeof treatment.collectRationale === "boolean"
      ? treatment.collectRationale
      : (typeof factors.collectRationale === "boolean" ? factors.collectRationale : undefined)
  );
  const treatmentName = String(
    treatment.name ||
      treatment.treatmentName ||
      factors.name ||
      factors.treatmentName ||
      ""
  ).toLowerCase();
  const treatmentFingerprint = (
    typeof treatment === "string"
      ? treatment
      : JSON.stringify(treatment || {})
  ).toLowerCase();
  const isFourTaskPilot = isFourTaskPilotCondition(treatment);
  const isBaseRateGroup3 = isBaseRateGroup3Condition(treatment);
  const isBaseRateStaticSyntheticGroup =
    isBaseRateStaticSyntheticGroupCondition(treatment);
  const usesShareableBaseRateGroup =
    isBaseRateGroup3 || isBaseRateStaticSyntheticGroup;
  // Post-peer conditions run the reflection activity as an in-game stage, so
  // the pre-lobby ends at the initial prediction: no aiChat, confirm,
  // confirmRationale, or polishReview step.
  const usesPostPeerReflection =
    getReflectionTiming(treatment) === "post-peer" && isBaseRateStaticSyntheticGroup;
  // The baseline arm runs no reflection activity at all. Its pre-lobby ends at
  // the initial prediction for the same reason the post-peer conditions' does,
  // so it reuses that collapse: no aiChat, confirm, confirmRationale, or
  // polishReview step. "none" is checked on its own rather than alongside the
  // synthetic-group predicate so that any future arm setting the mode inherits
  // the collapse instead of silently falling through to the Socratic chat.
  const usesNoPreLobbyActivity = preLobbyMode === "none";
  // Both flags mean the same thing here: the initial prediction is the last
  // pre-lobby screen and the skipped steps have to be back-filled.
  const endsPreLobbyAtInitial = usesPostPeerReflection || usesNoPreLobbyActivity;
  // The condition-specific instruction page sits between the initial estimate
  // and the preparation activity, so that nothing preceding the estimate and
  // the confidence rating depends on the assigned condition.
  //
  // Excluding endsPreLobbyAtInitial is load bearing twice. The baseline arm
  // runs no activity, so it has nothing to introduce, and effectiveStep below
  // collapses every step except fakeLobby to "initial" for those arms, which
  // would swallow the page anyway.
  const showsActivityInstructions =
    usesPostInitialConditionInstructions(treatment) && !endsPreLobbyAtInitial;
  // The post-peer follow-up arms hold the condition-specific instructions to
  // the same point, right after the initial estimate and confidence. Their
  // activity runs in-game after the peer review, so the page is the last
  // pre-lobby screen: acknowledging it ends the pre-lobby instead of opening
  // the activity. No arm fielded before these combines post-peer timing with
  // post-initial instructions, so no earlier arm reaches this path.
  const showsPostPeerActivityInstructions =
    usesPostInitialConditionInstructions(treatment) && usesPostPeerReflection;
  // askall conditions restrict the final answer to the participant's own
  // initial estimate or one of the two other participants' answers. The rule
  // is announced here, at the initial estimate, so it is never sprung on the
  // participant at the end. The ask-all follow-up takes a typed final answer
  // (postPeerAskAllOpenEntry), so it announces no rule.
  const postPeerClosedChoice = usesPostPeerClosedChoice(treatment);
  const askAllChoiceRuleNotice =
    "Note: after you submit your estimate, you will read two other participants' answers and reasoning. At the end of the study, your final answer must be one of these choices: keep your own estimate, or adopt one of the two other participants' answers. If some of these answers turn out to be identical, you will see fewer choices.";
  const usesTemplateQuestionnaire = preLobbyMode === "template";
  // The three base-rate synthetic-peer arms collect one short rationale on the
  // initial screen, before the reflection activity, in place of the 50-word
  // write-up that used to follow it. Same instrument in all three arms, so it
  // supplies the expertise annotation, the paste-detection surface, and the
  // participant's own opening message in the peer-review transcript without
  // sitting inside the contrast being estimated.
  const usesBriefRationale = usesBriefInitialRationale(treatment);
  // Thinned dose, set deliberately low. A word floor doubles as a stopping
  // point: participants write to the minimum and submit. 10 words buys roughly
  // one clause, which keeps the pre-treatment self-explanation dose as small as
  // it can be while still leaving text for the expertise annotation and the
  // outside-tool marker scan to work on. It is knowingly below the length at
  // which a rationale reliably carries diagnostic reasoning: in the July
  // write-ups the first reasoning marker sits at a median of word 24, and only
  // 12% of them show one inside the first 10 words. Protecting the coaching
  // contrast was judged worth that cost; raise this number if the annotation
  // turns out too thin to classify.
  const initialRationaleMinWords = usesBriefRationale ? 10 : 5;
  const usesNoInitialRationalePrompt =
    (preLobbyMode === "socratic" && collectRationaleSetting === false) ||
    treatmentName.includes("story-retire-pilot-nogroup-meta-norationale") ||
    treatmentName.includes("story-retire-pilot-nogroup-devil-norationale") ||
    treatmentName.includes("story-tax-pilot-nogroup-meta-norationale") ||
    treatmentName.includes("story-tax-pilot-nogroup-devil-norationale") ||
    treatmentName.includes("story-tax-math-socratic-nogroup-norationale") ||
    treatmentName.includes("story-tax-math-openend-socratic-nogroup-norationale") ||
    treatmentName.includes("story-baserate-socratic-nogroup-norationale") ||
    treatmentFingerprint.includes("story-retire-pilot-nogroup-meta-norationale") ||
    treatmentFingerprint.includes("story-retire-pilot-nogroup-devil-norationale") ||
    treatmentFingerprint.includes("story-tax-pilot-nogroup-meta-norationale") ||
    treatmentFingerprint.includes("story-tax-pilot-nogroup-devil-norationale") ||
    treatmentFingerprint.includes("story-tax-math-socratic-nogroup-norationale") ||
    treatmentFingerprint.includes("story-tax-math-openend-socratic-nogroup-norationale") ||
    treatmentFingerprint.includes("story-baserate-socratic-nogroup-norationale");
  const isPilotNoGroupMeta =
    treatmentName.includes("pilot-nogroup-meta") ||
    treatmentName.includes("pilot-tax-nogroup-meta") ||
    treatmentName.includes("story-retire-pilot-nogroup-meta") ||
    treatmentName.includes("story-retire-pilot-nogroup-devil") ||
    treatmentName.includes("story-retire-pilot-nogroup-template") ||
    treatmentName.includes("story-tax-pilot-nogroup-meta") ||
    treatmentName.includes("story-tax-pilot-nogroup-devil") ||
    treatmentName.includes("story-tax-pilot-nogroup-template") ||
    treatmentName.includes("story-tax-math-socratic-nogroup-norationale") ||
    treatmentName.includes("story-tax-math-nogroup-template") ||
    treatmentName.includes("story-tax-math-openend-socratic-nogroup-norationale") ||
    treatmentName.includes("story-tax-math-openend-nogroup-template") ||
    treatmentName.includes("story-baserate-socratic-nogroup-norationale") ||
    treatmentName.includes("story-baserate-nogroup-template") ||
    treatmentFingerprint.includes("pilot-nogroup-meta") ||
    treatmentFingerprint.includes("pilot-tax-nogroup-meta") ||
    treatmentFingerprint.includes("story-retire-pilot-nogroup-meta") ||
    treatmentFingerprint.includes("story-retire-pilot-nogroup-devil") ||
    treatmentFingerprint.includes("story-retire-pilot-nogroup-template") ||
    treatmentFingerprint.includes("story-tax-pilot-nogroup-meta") ||
    treatmentFingerprint.includes("story-tax-pilot-nogroup-devil") ||
    treatmentFingerprint.includes("story-tax-pilot-nogroup-template") ||
    treatmentFingerprint.includes("story-tax-math-socratic-nogroup-norationale") ||
    treatmentFingerprint.includes("story-tax-math-nogroup-template") ||
    treatmentFingerprint.includes("story-tax-math-openend-socratic-nogroup-norationale") ||
    treatmentFingerprint.includes("story-tax-math-openend-nogroup-template") ||
    treatmentFingerprint.includes("story-baserate-socratic-nogroup-norationale") ||
    treatmentFingerprint.includes("story-baserate-nogroup-template") ||
    // Fallback: story task with nogroup type
    ((treatment.task === "pension-reform" ||
      treatment.task === "flat-tax" ||
      treatment.task === "flat-tax-math" ||
      treatment.task === "flat-tax-math-openend" ||
      treatment.task === "base-rate" ||
      factors.task === "pension-reform" ||
      factors.task === "flat-tax" ||
      factors.task === "flat-tax-math" ||
      factors.task === "flat-tax-math-openend" ||
      factors.task === "base-rate") &&
      treatmentType === "nogroup");
  const isPilotTaxNoGroupMeta =
    treatmentName.includes("pilot-tax-nogroup-meta") ||
    treatmentName.includes("story-retire-pilot-nogroup-meta") ||
    treatmentName.includes("story-retire-pilot-nogroup-devil") ||
    treatmentName.includes("story-retire-pilot-nogroup-template") ||
    treatmentName.includes("story-tax-pilot-nogroup-template") ||
    treatmentName.includes("story-tax-pilot-nogroup-meta") ||
    treatmentName.includes("story-tax-pilot-nogroup-devil") ||
    treatmentName.includes("story-tax-math-socratic-nogroup-norationale") ||
    treatmentName.includes("story-tax-math-nogroup-template") ||
    treatmentName.includes("story-tax-math-openend-socratic-nogroup-norationale") ||
    treatmentName.includes("story-tax-math-openend-nogroup-template") ||
    treatmentName.includes("story-baserate-socratic-nogroup-norationale") ||
    treatmentName.includes("story-baserate-nogroup-template") ||
    treatmentFingerprint.includes("pilot-tax-nogroup-meta") ||
    treatmentFingerprint.includes("story-retire-pilot-nogroup-meta") ||
    treatmentFingerprint.includes("story-retire-pilot-nogroup-devil") ||
    treatmentFingerprint.includes("story-retire-pilot-nogroup-template") ||
    treatmentFingerprint.includes("story-tax-pilot-nogroup-template") ||
    treatmentFingerprint.includes("story-tax-pilot-nogroup-meta") ||
    treatmentFingerprint.includes("story-tax-pilot-nogroup-devil") ||
    treatmentFingerprint.includes("story-tax-math-socratic-nogroup-norationale") ||
    treatmentFingerprint.includes("story-tax-math-nogroup-template") ||
    treatmentFingerprint.includes("story-tax-math-openend-socratic-nogroup-norationale") ||
    treatmentFingerprint.includes("story-tax-math-openend-nogroup-template") ||
    treatmentFingerprint.includes("story-baserate-socratic-nogroup-norationale") ||
    treatmentFingerprint.includes("story-baserate-nogroup-template");
  // usesBriefRationale wins over both suppressors: the template arm sets
  // usesTemplateQuestionnaire and the Socratic arm sets
  // usesNoInitialRationalePrompt, and both still need the box.
  const shouldShowInitialRationaleBox =
    usesBriefRationale ||
    (isPilotTaxNoGroupMeta &&
      !usesNoInitialRationalePrompt &&
      !usesTemplateQuestionnaire);
  const shouldCollectRationale = typeof collectRationaleSetting === "boolean"
    ? collectRationaleSetting
    : !isPilotNoGroupMeta;
  const isNoAIControl = treatmentType === "No-AI Control";
  const isAICondition =
    treatmentType === "Control" ||
    treatmentType === "nogroup" ||
    treatmentType === "AI-Discuss" ||
    treatmentType === "Synthetic Peers";
  const shouldTrackPreLobbyPastes = isPasteEnabledNogroupCondition(treatment);
  const shouldDisableClipboard =
    taskConfig?.disableClipboard === true && !shouldTrackPreLobbyPastes;
  const changedAnswerConfidenceConfig = taskConfig?.changedAnswerConfidence || null;
  const shouldRequireInitialAnswerConfidenceOnConfirm =
    Boolean(changedAnswerConfidenceConfig) &&
    hasPredictionChanged(selectedPredictionBeforeAI, selectedPredictionAfterAI, taskConfig);
  // The post-treatment write-up screen is suppressed on every arm that sets
  // initialRationaleMode, whether the arm replaces the write-up with the brief
  // box ("brief") or collects no rationale at all ("none").
  // postConfirmWriteupMode itself stays on the treatment:
  // isBaseRateStaticSyntheticGroupCondition requires "group-shareable", so
  // clearing the factor would drop the arm out of the synthetic-peer machinery
  // in Stage.jsx and FinalVote.jsx.
  const requiresPostConfirmWriteup =
    Boolean(postConfirmWriteupMode) && !suppressesPostConfirmWriteup(treatment);
  const requiresPostConfirmPolish = Boolean(postConfirmPolishMode);
  const postConfirmWriteupMinimumWordCount =
    postConfirmWriteupMode === "convince-others"
      ? 50
      : usesShareableBaseRateGroup
        ? 50
        : 1;
  const groupContinuationButtonText = isBaseRateStaticSyntheticGroup
    ? "Continue to Group Review"
    : "Continue to Lobby";
  const groupContinuationDestination = isBaseRateStaticSyntheticGroup
    ? "the group review page"
    : "the lobby";
  const shouldUseFakeLobby =
    isBaseRateStaticSyntheticGroup && fakeLobbyConfig.enabled;
  const formattedInitialPrediction =
    formatPredictionValue(selectedPredictionBeforeAI, taskConfig) ||
    String(selectedPredictionBeforeAI || "");
  const preLobbyIntegrityStageName = (() => {
    if (isFourTaskPilot) return "pre-lobby-four-task-pilot";
    if (step === "quiz") return "pre-lobby-quiz";
    if (step === "activityInstructions") {
      return "pre-lobby-activity-instructions";
    }
    if (step === "aiChat" && usesTemplateQuestionnaire) {
      return "pre-lobby-template-questionnaire";
    }
    if (step === "aiChat") return "pre-lobby-ai-chat";
    if (step === "polishReview") return "pre-lobby-polish-review";
    if (step === "confirmRationale") return "pre-lobby-rationale-confirmation";
    if (step === "confirm") return "pre-lobby-confirm";
    return "pre-lobby-initial";
  })();

  const preLobbyPasteMirrorRef = useRef(null);
  const recordPreLobbyPaste = useCallback((behaviorEvent) => {
    if (!behaviorEvent?.pastedText) return;
    const detectedAt = Number(behaviorEvent.detectedAt || Date.now());
    const pasteEvent = {
      id: `prelobby-paste-${detectedAt}-${Math.random().toString(36).slice(2, 8)}`,
      detectedAt,
      stageId: "pre-lobby",
      source: behaviorEvent.stageName || preLobbyIntegrityStageName,
      fieldId: behaviorEvent.fieldId || null,
      treatmentName: treatmentNameExact,
      taskId: taskConfig?.id || factors.task || treatment.task || null,
      pastedText: behaviorEvent.pastedText,
      pastedTextLength: behaviorEvent.pastedTextLength || behaviorEvent.pastedText.length,
      phase: behaviorEvent.phase || "pre-lobby",
      provenance: behaviorEvent.provenance || "no_internal_provenance",
      matchedSourceType: behaviorEvent.matchedSourceType || null,
      matchedSourceAt: behaviorEvent.matchedSourceAt || null,
    };
    // Longest-wins, for the same reason the behaviour-tracking sink keeps a
    // mirror: a late server echo can roll the local array back, and a plain
    // read-modify-write would then append to the shorter value and drop a
    // paste. `outside_tool_usage.extract_paste_events` unions this log with
    // `behaviorTrackingEvents`, so a loss here is usually covered, but only
    // usually: the two logs are not supersets of one another.
    const existingEvents = Array.isArray(player.get("preLobbyPasteEvents"))
      ? player.get("preLobbyPasteEvents")
      : [];
    const mirrored = preLobbyPasteMirrorRef.current;
    const base =
      mirrored && mirrored.length > existingEvents.length ? mirrored : existingEvents;
    const updatedEvents = [...base, pasteEvent];
    preLobbyPasteMirrorRef.current = updatedEvents;
    player.set("preLobbyPasteEvents", updatedEvents);
    savePlayerMeta({
      player,
      name: "preLobbyPasteEvent",
      value: pasteEvent,
      debugTag: "Pre-lobby Paste Tracking",
    });
  }, [
    factors.task,
    player,
    preLobbyIntegrityStageName,
    taskConfig?.id,
    treatment.task,
    treatmentNameExact,
  ]);

  useIntegrityTracking({
    enabled: Boolean(player?.id),
    player,
    phase: "pre-lobby",
    defaultStageId: "pre-lobby",
    defaultStageName: preLobbyIntegrityStageName,
    onPreLobbyPaste: recordPreLobbyPaste,
  });

  const resolvePostQuizStep = () => {
    const beforeAI = player.get("preLobbyPredictionBeforeAI");
    const afterAI = player.get("preLobbyPredictionAfterAI");
    const assistantCompleted = player.get("preLobbyAssistantCompleted");
    const rationaleConfirmed =
      player.get("preLobbyRationaleConfirmed") || !requiresPostConfirmWriteup;
    const fakeLobbyStartedAt = Number(
      player.get("preLobbyFakeLobbyStartedAt") || 0
    );
    const fakeLobbyCompleted = player.get("preLobbyFakeLobbyCompleted") === true;
    const existingExplanation =
      player.get("preLobbyShareableRationale") ||
      player.get("preLobbyPolishedRationale") ||
      player.get("preLobbyShareableRationaleDraft") ||
      player.get("preLobbyExplanation");
    const hasPolishDraft = Boolean(
      String(player.get("preLobbyShareableRationaleDraft") || "").trim()
    );
    const hasPolishSuggestion = Boolean(
      String(player.get("preLobbyPolishedRationale") || "").trim()
    );
    const polishTriggered = Boolean(player.get("preLobbyPolishTrigger"));
    if (existingExplanation) {
      setInitialExplanation(existingExplanation);
    }

    if (
      shouldUseFakeLobby &&
      fakeLobbyStartedAt > 0 &&
      !fakeLobbyCompleted &&
      !player.get("preLobbyCompleted")
    ) {
      return "fakeLobby";
    }

    // The post-peer conditions and the baseline arm have no pre-lobby step
    // after the initial prediction, so a returning participant must never
    // resume at aiChat, confirm, confirmRationale, or polishReview. The
    // fake-lobby check above already handled the only other pre-lobby screen
    // these conditions can show.
    if (endsPreLobbyAtInitial) {
      if (beforeAI) {
        setSelectedPredictionBeforeAI(beforeAI);
        setSelectedPredictionAfterAI(afterAI || beforeAI);
        // A reload on the post-peer instruction page comes back to it, for the
        // same reason as the pre-peer branch below.
        if (
          showsPostPeerActivityInstructions &&
          player.get("preLobbyActivityInstructionsAcked") !== true
        ) {
          return "activityInstructions";
        }
      }
      return "initial";
    }

    if (beforeAI && !assistantCompleted) {
      // Seed the selection before either return. ReflectionAssistantChat only
      // sends its opening message when predictionForOpener is truthy, so a
      // Socratic participant who resumes without this lands in an empty chat.
      setSelectedPredictionBeforeAI(beforeAI);
      // A reload on the instruction page has to come back to the instruction
      // page. Falling through to aiChat would start the activity for someone
      // who never finished reading what the activity is.
      if (
        showsActivityInstructions &&
        player.get("preLobbyActivityInstructionsAcked") !== true
      ) {
        return "activityInstructions";
      }
      return "aiChat";
    } else if (beforeAI && assistantCompleted && !afterAI) {
      setSelectedPredictionBeforeAI(beforeAI);
      return "confirm";
    } else if (afterAI && !rationaleConfirmed) {
      setSelectedPredictionBeforeAI(beforeAI);
      setSelectedPredictionAfterAI(afterAI);
      if (
        requiresPostConfirmPolish &&
        (hasPolishDraft || hasPolishSuggestion || polishTriggered)
      ) {
        return "polishReview";
      }
      return "confirmRationale";
    } else if (afterAI) {
      setSelectedPredictionBeforeAI(beforeAI);
      setSelectedPredictionAfterAI(afterAI);
      return "confirm";
    }
    return "initial";
  };

  const completePreLobbyFlow = useCallback(
    (timestamp = Date.now()) => {
      // The server copies `behaviorTrackingEvents` into playerBeliefs and the
      // round eventLog when the game starts, so every pre-lobby event has to be
      // written before the step that starts the game.
      flushBehaviorTracking();
      player.set("preLobbyCompleted", true);
      player.set("preLobbyCompletedAt", timestamp);
      next();
    },
    [next, player]
  );

  const startFakeLobbyOrComplete = useCallback(
    (timestamp = Date.now()) => {
      if (shouldUseFakeLobby) {
        let durationSeconds = Number(
          player.get("preLobbyFakeLobbyDurationSeconds") || 0
        );
        const durationIsValid =
          Number.isInteger(durationSeconds) &&
          durationSeconds >= fakeLobbyConfig.minSeconds &&
          durationSeconds <= fakeLobbyConfig.maxSeconds;
        if (!durationIsValid) {
          durationSeconds =
            Math.floor(
              Math.random() *
                (fakeLobbyConfig.maxSeconds - fakeLobbyConfig.minSeconds + 1)
            ) + fakeLobbyConfig.minSeconds;
        }

        const existingStart = Number(
          player.get("preLobbyFakeLobbyStartedAt") || 0
        );
        const startedAt = existingStart > 0 ? existingStart : timestamp;

        player.set("preLobbyFakeLobbyStartedAt", startedAt);
        player.set("preLobbyFakeLobbyDurationSeconds", durationSeconds);
        player.set(
          "preLobbyFakeLobbyEndsAt",
          startedAt + durationSeconds * 1000
        );
        player.set("preLobbyFakeLobbyCompleted", false);
        setStep("fakeLobby");
        return;
      }

      completePreLobbyFlow(timestamp);
    },
    [completePreLobbyFlow, fakeLobbyConfig.maxSeconds, fakeLobbyConfig.minSeconds, player, shouldUseFakeLobby]
  );

  const handleFakeLobbyComplete = useCallback(() => {
    const timestamp = Date.now();
    player.set("preLobbyFakeLobbyCompleted", true);
    player.set("preLobbyFakeLobbyCompletedAt", timestamp);
    completePreLobbyFlow(timestamp);
  }, [completePreLobbyFlow, player]);

  // Load existing state if available
  useEffect(() => {
    if (!quizEnabled) {
      setStep(resolvePostQuizStep());
      return;
    }
    const quizDone = player.get("quizCompleted");
    if (!quizDone) {
      setStep("quiz");
      return;
    }
    setStep(resolvePostQuizStep());
  }, [quizEnabled]);

  useEffect(() => {
    if (!shouldDisableClipboard) return;

    function blockClipboardActions(event) {
      const key = String(event.key || "").toLowerCase();
      const usingModifier = event.metaKey || event.ctrlKey;
      const blockedShortcut =
        usingModifier && (key === "c" || key === "v" || key === "x");

      if (
        event.type === "copy" ||
        event.type === "cut" ||
        event.type === "paste" ||
        event.type === "contextmenu" ||
        event.type === "dragstart" ||
        blockedShortcut
      ) {
        event.preventDefault();
      }
    }

    document.addEventListener("copy", blockClipboardActions, true);
    document.addEventListener("cut", blockClipboardActions, true);
    document.addEventListener("paste", blockClipboardActions, true);
    document.addEventListener("contextmenu", blockClipboardActions, true);
    document.addEventListener("dragstart", blockClipboardActions, true);
    document.addEventListener("keydown", blockClipboardActions, true);

    return () => {
      document.removeEventListener("copy", blockClipboardActions, true);
      document.removeEventListener("cut", blockClipboardActions, true);
      document.removeEventListener("paste", blockClipboardActions, true);
      document.removeEventListener("contextmenu", blockClipboardActions, true);
      document.removeEventListener("dragstart", blockClipboardActions, true);
      document.removeEventListener("keydown", blockClipboardActions, true);
    };
  }, [shouldDisableClipboard]);

  if (isFourTaskPilot) {
    return <FourTaskPilotTask next={next} />;
  }

  const handleInitialPredictionSelect = (choice) => {
    // Only update the selected state, don't advance
    setSelectedPredictionBeforeAI(choice);
  };

  const handleInitialPredictionSubmit = (choice, explanation, confidence) => {
    const trimmedExplanation = (explanation || "").trim();
    if (
      shouldShowInitialRationaleBox &&
      countWords(trimmedExplanation) < initialRationaleMinWords
    ) {
      alert("You need to write more.");
      return;
    }
    const timestamp = Date.now();
    // Store prediction before AI conversation
    player.set("preLobbyPredictionBeforeAI", choice);
    player.set("preLobbyPredictionBeforeAIAt", timestamp);
    if (trimmedExplanation) {
      player.set("preLobbyExplanation", trimmedExplanation);
      player.set("preLobbyExplanationAt", timestamp);
      if (usesBriefRationale) {
        // Everything downstream keys off preLobbyShareableRationale: the seeded
        // peer-review transcript (buildShareableRationaleSeedMessage in
        // callbacks.js), the CSV export, the first entry of MARKER_TEXT_FIELDS
        // in the outside-tool scan, and the peer pools harvested for later
        // waves. Mirroring it here keeps all of that working now that the
        // post-treatment write-up no longer fills it.
        player.set("preLobbyShareableRationale", trimmedExplanation);
        player.set("preLobbyShareableRationaleAt", timestamp);
      }
      recordSubmittedIntegrityText({
        playerId: player.id,
        fieldId: "prelobby-initial-rationale",
        text: trimmedExplanation,
        phase: "pre-lobby",
        stageId: "pre-lobby",
        stageName: "pre-lobby-initial",
        detectedAt: timestamp,
      });
    }
    player.set("preLobbyConfidenceBeforeAI", confidence);
    player.set("preLobbyConfidenceBeforeAIAt", timestamp);

    if (endsPreLobbyAtInitial) {
      // The pre-lobby ends here. Mirror the initial values into the fields that
      // downstream code reads (the game-start transfer, the peer-review stage,
      // and the CSV export all key off the "afterAI" fields) and mark the
      // skipped steps complete so nothing waits on them.
      player.set("preLobbyPredictionAfterAI", choice);
      player.set("preLobbyPredictionAfterAIAt", timestamp);
      player.set("preLobbyConfidenceAfterAI", confidence);
      player.set("preLobbyConfidenceAfterAIAt", timestamp);
      player.set("preLobbyPrediction", choice);
      player.set("preLobbyPredictionAt", timestamp);
      player.set("preLobbyConfidence", confidence);
      player.set("preLobbyConfidenceAt", timestamp);
      player.set("preLobbyAssistantCompleted", true);
      player.set("preLobbyAssistantCompletedAt", timestamp);
      player.set("preLobbyRationaleConfirmed", true);
      player.set("preLobbyRationaleConfirmedAt", timestamp);
      setSelectedPredictionAfterAI(choice);
      savePlayerMeta({
        player,
        name: "initialPrediction",
        value: { prediction: choice, confidence },
        debugTag: usesNoPreLobbyActivity
          ? "Baseline Condition: initial prediction"
          : "Post-peer Condition: initial prediction",
      });
      if (showsPostPeerActivityInstructions) {
        // Every value above is already recorded, so the page adds no
        // elicitation. The pre-lobby ends when it is acknowledged.
        if (!player.get("preLobbyActivityInstructionsShownAt")) {
          player.set("preLobbyActivityInstructionsShownAt", timestamp);
        }
        setStep("activityInstructions");
        return;
      }
      startFakeLobbyOrComplete(timestamp);
      return;
    }

    if (showsActivityInstructions) {
      // Only on the first render. A reload re-enters this page and must not
      // reset the clock, so the dwell time stays measurable.
      if (!player.get("preLobbyActivityInstructionsShownAt")) {
        player.set("preLobbyActivityInstructionsShownAt", timestamp);
      }
      setStep("activityInstructions");
      return;
    }

    // Move to AI chat step
    setStep("aiChat");
  };

  const handleActivityInstructionsContinue = () => {
    const timestamp = Date.now();
    player.set("preLobbyActivityInstructionsAcked", true);
    player.set("preLobbyActivityInstructionsAckedAt", timestamp);
    if (showsPostPeerActivityInstructions) {
      startFakeLobbyOrComplete(timestamp);
      return;
    }
    setStep("aiChat");
  };

  const handleAIChatComplete = () => {
    // Mark pre-lobby follow-up step as completed
    player.set("preLobbyAssistantCompleted", true);
    player.set("preLobbyAssistantCompletedAt", Date.now());
    const preLobbyMessages = player.get("preLobbyMessages") || [];
    savePlayerMeta({ player, name: "preLobbyMessages", value: preLobbyMessages, debugTag: "Pre-lobby Follow-up:1" });

    // Move to confirmation step
    setStep("confirm");
  };

  const handleConfirmPredictionSelect = (choice) => {
    // Only update the selected state, don't advance
    setSelectedPredictionAfterAI(choice);
  };

  const handleConfirmPredictionSubmit = (choice, explanation, confidence, initialAnswerConfidence) => {
    const trimmedExplanation = (explanation || "").trim();
    const changedAnswer = hasPredictionChanged(
      selectedPredictionBeforeAI,
      choice,
      taskConfig
    );
    const changedAnswerConfidenceError = getChangedAnswerConfidenceValidationError({
      changedAnswer,
      finalConfidence: confidence,
      initialAnswerConfidence,
      config: taskConfig?.changedAnswerConfidence,
    });
    if (changedAnswerConfidenceError) {
      alert(changedAnswerConfidenceError);
      return;
    }
    const timestamp = Date.now();
    // Store prediction after AI conversation
    player.set("preLobbyPredictionAfterAI", choice);
    player.set("preLobbyPredictionAfterAIAt", timestamp);
    player.set("preLobbyConfidenceAfterAI", confidence);
    player.set("preLobbyConfidenceAfterAIAt", timestamp);
    player.set(
      "preLobbyConfidenceForInitialAnswerAfterAI",
      changedAnswer ? initialAnswerConfidence : null
    );
    player.set(
      "preLobbyConfidenceForInitialAnswerAfterAIAt",
      changedAnswer ? timestamp : null
    );

    // Store as the main initial prediction (for backward compatibility)
    player.set("preLobbyPrediction", choice);
    player.set("preLobbyPredictionAt", timestamp);
    player.set("preLobbyConfidence", confidence);
    player.set("preLobbyConfidenceAt", timestamp);
    if (trimmedExplanation) {
      player.set("preLobbyExplanation", trimmedExplanation);
      player.set("preLobbyExplanationAt", timestamp);
      recordSubmittedIntegrityText({
        playerId: player.id,
        fieldId: "prelobby-confirm-rationale",
        text: trimmedExplanation,
        phase: "pre-lobby",
        stageId: "pre-lobby",
        stageName: "pre-lobby-confirm",
        detectedAt: timestamp,
      });
    }

    if (requiresPostConfirmWriteup) {
      setInitialExplanation(
        player.get("preLobbyShareableRationale") ||
          player.get("preLobbyShareableRationaleDraft") ||
          trimmedExplanation
      );
      setStep("confirmRationale");
      return;
    }

    // Skip rationale confirmation for AI conditions
    player.set("preLobbyRationaleConfirmed", true);
    player.set("preLobbyRationaleConfirmedAt", timestamp);
    startFakeLobbyOrComplete(timestamp);
  };

  const handleConfirmRationaleSubmit = (explanation) => {
    const trimmedExplanation = (explanation || "").trim();
    if (countWords(trimmedExplanation) < postConfirmWriteupMinimumWordCount) {
      return;
    }
    const timestamp = Date.now();

    if (requiresPostConfirmPolish) {
      player.set("preLobbyShareableRationaleDraft", trimmedExplanation);
      player.set("preLobbyShareableRationaleDraftAt", timestamp);
      player.set("preLobbyShareableRationale", "");
      player.set("preLobbyShareableRationaleAt", null);
      player.set("preLobbyPolishedRationale", "");
      player.set("preLobbyPolishedRationaleAt", null);
      player.set("preLobbyPolishAiErrors", []);
      savePlayerMeta({
        player,
        name: "preLobbyShareableRationaleDraft",
        value: { rationale: trimmedExplanation, timestamp },
        debugTag: "Pre-lobby Shareable Rationale Draft",
      });
      recordSubmittedIntegrityText({
        playerId: player.id,
        fieldId: "prelobby-rationale-confirmation-rationale",
        text: trimmedExplanation,
        phase: "pre-lobby",
        stageId: "pre-lobby",
        stageName: "pre-lobby-rationale-confirmation",
        detectedAt: timestamp,
      });
      player.set("preLobbyPolishTrigger", timestamp);
      setInitialExplanation(trimmedExplanation);
      setStep("polishReview");
      return;
    }

    player.set("preLobbyShareableRationale", trimmedExplanation);
    player.set("preLobbyShareableRationaleAt", timestamp);
    player.set("preLobbyExplanation", trimmedExplanation);
    player.set("preLobbyExplanationAt", timestamp);
    savePlayerMeta({
      player,
      name: "preLobbyShareableRationale",
      value: { rationale: trimmedExplanation, timestamp },
      debugTag: "Pre-lobby Shareable Rationale",
    });
    recordSubmittedIntegrityText({
      playerId: player.id,
      fieldId: "prelobby-rationale-confirmation-rationale",
      text: trimmedExplanation,
      phase: "pre-lobby",
      stageId: "pre-lobby",
      stageName: "pre-lobby-rationale-confirmation",
      detectedAt: timestamp,
    });
    player.set("preLobbyRationaleConfirmed", true);
    player.set("preLobbyRationaleConfirmedAt", timestamp);

    // Mark as completed
    startFakeLobbyOrComplete(timestamp);
  };

  const handlePolishReviewSubmit = (finalRationale) => {
    const trimmedExplanation = String(finalRationale || "").trim();
    if (!trimmedExplanation) {
      return;
    }
    const timestamp = Date.now();
    player.set("preLobbyShareableRationale", trimmedExplanation);
    player.set("preLobbyShareableRationaleAt", timestamp);
    player.set("preLobbyExplanation", trimmedExplanation);
    player.set("preLobbyExplanationAt", timestamp);
    savePlayerMeta({
      player,
      name: "preLobbyShareableRationale",
      value: { rationale: trimmedExplanation, timestamp },
      debugTag: "Pre-lobby Shareable Rationale Final",
    });
    recordSubmittedIntegrityText({
      playerId: player.id,
      fieldId: "prelobby-polish-review-rationale",
      text: trimmedExplanation,
      phase: "pre-lobby",
      stageId: "pre-lobby",
      stageName: "pre-lobby-polish-review",
      detectedAt: timestamp,
    });
    player.set("preLobbyRationaleConfirmed", true);
    player.set("preLobbyRationaleConfirmedAt", timestamp);
    startFakeLobbyOrComplete(timestamp);
  };

  if (quizEnabled && step === "quiz") {
    return <QuizStage onComplete={() => setStep(resolvePostQuizStep())} />;
  }

  const initialPredictionPageCopy = getInitialPredictionPageCopy(taskConfig);

  if (isNoAIControl) {
    // No-AI Control: prediction with required rationale
    return (
      <PredictionButtonPage
        onPredictionSelect={(choice) => {
          setSelectedPredictionBeforeAI(choice);
        }}
        onPredictionSubmit={(choice, explanation, confidence) => {
          const trimmedExplanation = (explanation || "").trim();
          if (!trimmedExplanation && !isPilotNoGroupMeta) {
            return;
          }
          const timestamp = Date.now();
          // Store prediction data
          player.set("preLobbyPrediction", choice);
          player.set("preLobbyPredictionAt", timestamp);
          if (trimmedExplanation) {
            player.set("preLobbyExplanation", trimmedExplanation);
            player.set("preLobbyExplanationAt", timestamp);
            recordSubmittedIntegrityText({
              playerId: player.id,
              fieldId: "prelobby-noai-rationale",
              text: trimmedExplanation,
              phase: "pre-lobby",
              stageId: "pre-lobby",
              stageName: "pre-lobby-no-ai-control",
              detectedAt: timestamp,
            });
          }
          player.set("preLobbyConfidence", confidence);
          player.set("preLobbyConfidenceAt", timestamp);
          const meta = {
            prediction: choice,
            explanation: trimmedExplanation,
            confidence
          };
          savePlayerMeta({ player, name: "initialPrediction", value: meta, debugTag: "No-AI Control Condition:1" });
          
          // Mark as completed and advance
          startFakeLobbyOrComplete(Date.now());
        }}
        selectedPrediction={selectedPredictionBeforeAI}
        options={taskConfig.options}
        predictionInput={taskConfig.predictionInput}
        predictionDisplayFormatter={(value) => formatPredictionValue(value, taskConfig)}
        questionText={taskConfig.questionText}
        introPrompt={initialPredictionPageCopy.introPrompt}
        caseNotice={taskConfig.caseNotice}
        DetailsComponent={taskConfig.DetailsComponent}
        {...(shouldCollectRationale ? {
          rationale: initialExplanation,
          onRationaleChange: setInitialExplanation,
          requireRationale: true,
          rationaleLabel: "Please explain your rationale in depth",
          rationalePlaceholder: "Write a detailed explanation of why you chose this prediction."
        } : {})}
        confidence={initialConfidence}
        onConfidenceChange={setInitialConfidence}
        requireConfidence={true}
        confidenceMin={taskConfig.confidenceMin}
        confidenceMax={taskConfig.confidenceMax}
        requireWholeNumberConfidence={taskConfig.requireWholeNumberConfidence}
        confidenceLabel={taskConfig.confidenceFieldLabel}
        confidencePlaceholder={taskConfig.confidencePlaceholder}
        confidenceHelpText={taskConfig.confidenceHelpText}
        confidenceErrorText={taskConfig.confidenceErrorText}
        integrityNotice={taskConfig.integrityNotice}
        integrityFieldPrefix="prelobby-noai"
        title="Initial Prediction"
        description={initialPredictionPageCopy.description}
      />
    );
  } else if (isAICondition) {
    // Control, AI-Discuss, Synthetic Peers: Three-step flow.
    // For the post-peer conditions and the baseline arm the only pre-lobby
    // screens are the initial prediction and the fake lobby, so the reflection
    // activity, the confirm step, the writeup, and the polish step can never
    // render here.
    // The post-peer follow-up arms add one screen to that pair: the
    // condition-specific instruction page that follows the initial estimate.
    const effectiveStep =
      endsPreLobbyAtInitial &&
      step !== "fakeLobby" &&
      !(showsPostPeerActivityInstructions && step === "activityInstructions")
        ? "initial"
        : step;
    if (effectiveStep === "initial") {
      return (
        <PredictionButtonPage
          onPredictionSelect={handleInitialPredictionSelect}
          onPredictionSubmit={handleInitialPredictionSubmit}
          selectedPrediction={selectedPredictionBeforeAI}
          options={taskConfig.options}
          predictionInput={taskConfig.predictionInput}
          predictionDisplayFormatter={(value) => formatPredictionValue(value, taskConfig)}
          questionText={taskConfig.questionText}
          introPrompt={initialPredictionPageCopy.introPrompt}
          caseNotice={postPeerClosedChoice ? askAllChoiceRuleNotice : taskConfig.caseNotice}
          DetailsComponent={taskConfig.DetailsComponent}
          {...(shouldShowInitialRationaleBox ? {
            rationale: initialExplanation,
            onRationaleChange: setInitialExplanation,
            requireRationale: true,
            minRationaleWords: initialRationaleMinWords,
            // Descriptive framing, not justificatory. "What is the main reason
            // for your answer" is the template arm's first box, so asking it
            // here would hand every arm the template's opening move.
            rationaleLabel: usesBriefRationale
              ? "In one sentence, how did you arrive at this answer?"
              : "Please explain the rationale for your choice (at least 2 sentences)",
            rationalePlaceholder: usesBriefRationale
              ? "Describe how you got to this answer."
              : "Explain why you chose this prediction."
          } : {})}
          confidence={initialConfidence}
          onConfidenceChange={setInitialConfidence}
          requireConfidence={true}
          confidenceMin={taskConfig.confidenceMin}
          confidenceMax={taskConfig.confidenceMax}
          requireWholeNumberConfidence={taskConfig.requireWholeNumberConfidence}
          confidenceLabel={taskConfig.confidenceFieldLabel}
          confidencePlaceholder={taskConfig.confidencePlaceholder}
          confidenceHelpText={taskConfig.confidenceHelpText}
          confidenceErrorText={taskConfig.confidenceErrorText}
          integrityNotice={taskConfig.integrityNotice}
          integrityFieldPrefix="prelobby-initial"
          title="Initial Prediction"
          description={initialPredictionPageCopy.description}
        />
      );
    } else if (effectiveStep === "activityInstructions") {
      return (
        <ActivityInstructions onContinue={handleActivityInstructionsContinue} />
      );
    } else if (effectiveStep === "aiChat") {
      if (usesTemplateQuestionnaire) {
        return (
          <ReflectionTemplateQuestionnaire
            next={handleAIChatComplete}
            taskConfig={taskConfig}
            keys={PRE_LOBBY_REFLECTION_KEYS}
            showDetailsToggle={isBaseRateStaticSyntheticGroup}
          />
        );
      }
      return (
        <ReflectionAssistantChat
          next={handleAIChatComplete}
          predictionForOpener={selectedPredictionBeforeAI}
          taskConfig={taskConfig}
          keys={PRE_LOBBY_REFLECTION_KEYS}
          showDetailsToggle={isBaseRateStaticSyntheticGroup}
        />
      );
    } else if (effectiveStep === "confirm") {
      return (
        <PredictionButtonPage
          onPredictionSelect={handleConfirmPredictionSelect}
          onPredictionSubmit={handleConfirmPredictionSubmit}
          selectedPrediction={selectedPredictionAfterAI}
          options={taskConfig.options}
          predictionInput={taskConfig.predictionInput}
          predictionDisplayFormatter={(value) => formatPredictionValue(value, taskConfig)}
          questionText={taskConfig.questionText}
          introPrompt={isBaseRateStaticSyntheticGroup ? "" : taskConfig.introPrompt}
          DetailsComponent={taskConfig.DetailsComponent}
          {...(shouldCollectRationale ? {
            rationale: initialExplanation,
            onRationaleChange: setInitialExplanation,
            requireRationale: true,
            rationaleLabel: "State your rationale",
            rationalePlaceholder: "Write a brief explanation of why you chose this prediction."
          } : {})}
          confidence={confirmConfidence}
          onConfidenceChange={setConfirmConfidence}
          requireConfidence={true}
          confidenceMin={taskConfig.confidenceMin}
          confidenceMax={taskConfig.confidenceMax}
          requireWholeNumberConfidence={taskConfig.requireWholeNumberConfidence}
          confidenceLabel={taskConfig.confidenceFieldLabel}
          confidencePlaceholder={taskConfig.confidencePlaceholder}
          confidenceHelpText={taskConfig.confidenceHelpText}
          confidenceErrorText={taskConfig.confidenceErrorText}
          {...(shouldRequireInitialAnswerConfidenceOnConfirm
            ? {
                secondaryConfidence: confirmInitialAnswerConfidence,
                onSecondaryConfidenceChange: setConfirmInitialAnswerConfidence,
                requireSecondaryConfidence: true,
                secondaryConfidenceMin: taskConfig.changedAnswerConfidence?.min ?? 0,
                secondaryConfidenceMax: taskConfig.changedAnswerConfidence?.max ?? 100,
                requireWholeNumberSecondaryConfidence:
                  taskConfig.changedAnswerConfidence?.requireWholeNumber === true,
                secondaryConfidenceLabel: taskConfig.changedAnswerConfidence?.label,
                secondaryConfidencePlaceholder: taskConfig.changedAnswerConfidence?.placeholder,
                secondaryConfidenceHelpText: taskConfig.changedAnswerConfidence?.helpText,
                secondaryConfidenceErrorText: taskConfig.changedAnswerConfidence?.errorText,
                secondaryConfidenceCombinedMax: taskConfig.changedAnswerConfidence?.combinedMax,
              }
            : {})}
          integrityNotice={taskConfig.integrityNotice}
          integrityFieldPrefix="prelobby-confirm"
          title="Confirm Your Belief"
          description={taskConfig.confirmDescriptionTemplate
            ? taskConfig.confirmDescriptionTemplate(formattedInitialPrediction)
            : usesTemplateQuestionnaire
              ? `Your initial prediction was: ${formattedInitialPrediction}. Now that you've answered the follow-up questions, please confirm your final prediction (you may keep it the same or change it).${changedAnswerConfidenceConfig ? " If you change your answer, you will also report how much confidence you still assign to your initial answer." : ""}`
              : `Your initial prediction was: ${formattedInitialPrediction}. Now that you've discussed your reasoning with the AI assistant, please confirm your prediction (you may keep it the same or change it).${changedAnswerConfidenceConfig ? " If you change your answer, you will also report how much confidence you still assign to your initial answer." : ""}${requiresPostConfirmWriteup ? requiresPostConfirmPolish ? " After confirming, you will complete one final writeup step and then review an AI-polished version before entering the lobby." : " After confirming, you will complete one final writeup step." : " After confirming, you will proceed to the lobby and wait for other players to form the game."}`}
          showLoanDetails={false}
          collapsibleDetails
          detailsToggle={taskConfig.detailsToggle}
          detailsTitle={taskConfig.detailsTitle}
          buttonText="Continue"
        />
      );
    } else if (effectiveStep === "confirmRationale") {
      return (
        <RationaleConfirmationPage
          prediction={formatPredictionValue(selectedPredictionAfterAI, taskConfig)}
          rationale={initialExplanation}
          onRationaleChange={setInitialExplanation}
          onSubmit={handleConfirmRationaleSubmit}
          transcriptMessages={player.get("preLobbyMessages") || []}
          preLobbyMode={preLobbyMode}
          writeupMode={postConfirmWriteupMode}
          polishMode={postConfirmPolishMode}
          buttonText={
            requiresPostConfirmPolish
              ? "Continue to AI Polish"
              : postConfirmWriteupMode === "convince-others"
                ? "Continue"
                : groupContinuationButtonText
          }
          integrityFieldPrefix="prelobby-rationale-confirmation"
          minimumWordCount={postConfirmWriteupMinimumWordCount}
          collapseTranscriptByDefault={postConfirmWriteupMinimumWordCount >= 50}
          showDuringGroupReview={isBaseRateStaticSyntheticGroup}
          taskConfig={isBaseRateStaticSyntheticGroup ? taskConfig : null}
        />
      );
    } else if (effectiveStep === "polishReview") {
      return (
        <PolishReviewPage
          originalRationale={
            player.get("preLobbyShareableRationaleDraft") || initialExplanation
          }
          initialFinalRationale={
            player.get("preLobbyShareableRationale") ||
            player.get("preLobbyPolishedRationale") ||
            player.get("preLobbyShareableRationaleDraft") ||
            initialExplanation
          }
          onSubmit={handlePolishReviewSubmit}
          buttonText={groupContinuationButtonText}
          continuationDestination={groupContinuationDestination}
          taskConfig={isBaseRateStaticSyntheticGroup ? taskConfig : null}
        />
      );
    } else if (effectiveStep === "fakeLobby") {
      return (
        <FakeLobbyPage
          player={player}
          minSeconds={fakeLobbyConfig.minSeconds}
          maxSeconds={fakeLobbyConfig.maxSeconds}
          basePaymentAmountText={compensation.basePaymentAmountText}
          onComplete={handleFakeLobbyComplete}
        />
      );
    }
  } else {
    // Fallback: Use original assistant chat interface
    return (
      <ReflectionAssistantChat
        next={next}
        taskConfig={taskConfig}
        keys={PRE_LOBBY_REFLECTION_KEYS}
      />
    );
  }
}
